import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TodayIcon from '@mui/icons-material/Today';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Hidden,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import WorktimeTimeline from '../Bookings/components/WorktimeTimeline';
import useInternalBooking from '../../hooks/useInternalBooking';
import useBranch from '../../hooks/useBranch';
import useWorktime from '../../hooks/useWorktime';
import EntertainmentCapacity, {
  REQUESTED_HATCH_IMAGE,
} from '../Bookings/components/EntertainmentCapacity';
import LegendGradient from '../Bookings/components/LegendGradient';
import ResourceLegend from '../Bookings/BookingGrid/common/ResourceLegend';
import { createResourceColorById } from '../../util/resourceColorPool';

import AuthContext from '../../context/authContext';
import DotsLoader from '../ui/DotsLoader';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const toIdString = value => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return String(value?._id ?? value?.id ?? value);
};

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

const ResourceBookingsAccordion = React.memo(function ResourceBookingsAccordion({ entertainmentData }) {
  const resources = Array.isArray(entertainmentData?.resources) ? entertainmentData.resources : [];
  const internalBookings = Array.isArray(entertainmentData?.internalBookings)
    ? entertainmentData.internalBookings
    : [];
  const timelineViewportRef = useRef(null);
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0);

  const hasDirectResourceBooking = useMemo(
    () => internalBookings.some(b => b?.resourceId != null),
    [internalBookings]
  );
  const hasOrderedResourceBooking = useMemo(
    () =>
      internalBookings.some(b => Array.isArray(b?.orderedResourceIds) && b.orderedResourceIds.length > 0),
    [internalBookings]
  );

  // Only show this expandable section for direct (resourceId-based) assignments.
  // Ordered-resource bookings do not represent a single concrete resource assignment.
  const show = !hasOrderedResourceBooking && resources.length > 0 && hasDirectResourceBooking;
  const resourceColorById = useMemo(() => createResourceColorById(resources), [resources]);
  const theme = useTheme();
  const timelineChrome = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    return {
      outerBorder: `1px solid ${
        isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.08)
      }`,
      headerCellBorderRight: `1px solid ${
        isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.06)
      }`,
      headerBg: isLight ? theme.palette.grey[200] : alpha(theme.palette.common.black, 0.75),
      trackBg: isLight ? theme.palette.grey[200] : alpha(theme.palette.common.black, 0.18),
      gridHour: isLight ? theme.palette.grey[400] : alpha(theme.palette.common.white, 0.1),
      gridHalf: isLight ? theme.palette.grey[300] : alpha(theme.palette.common.white, 0.05),
      barBorder: `1px solid ${
        isLight ? theme.palette.grey[400] : alpha(theme.palette.common.white, 0.18)
      }`,
      barBg: isLight
        ? alpha(theme.palette.common.black, 0.04)
        : alpha(theme.palette.common.white, 0.06),
      labelBg: isLight
        ? alpha(theme.palette.common.white, 0.92)
        : alpha(theme.palette.common.black, 0.45),
      chipBg: isLight
        ? theme.palette.action.selected
        : alpha(theme.palette.common.white, 0.08),
    };
  }, [theme]);

  useEffect(() => {
    const el = timelineViewportRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(entries => {
      const next = Math.floor(entries?.[0]?.contentRect?.width || 0);
      setTimelineViewportWidth(next);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const resourceById = useMemo(() => {
    const map = new Map();
    for (const r of resources) {
      const id = toIdString(r?._id);
      if (!id) continue;
      map.set(id, r);
    }
    return map;
  }, [resources]);

  const getResourceLabel = resourceId => {
    const rid = toIdString(resourceId);
    if (!rid) return '—';
    const r = resourceById.get(rid);
    return r?.name || '—';
  };

  const timeline = useMemo(() => {
    const START_HOUR = 9;
    const END_HOUR = 24;
    const BAR_HEIGHT = 28;
    const BAR_GAP = 8;

    const maxDayjs = (a, b) => (a.isAfter(b) ? a : b);
    const minDayjs = (a, b) => (a.isBefore(b) ? a : b);

    const directBookings = internalBookings.filter(b => b?.resourceId != null);
    const baseDay = directBookings[0]?.startTime
      ? dayjs(directBookings[0].startTime)
      : dayjs();
    const dayStart = baseDay.startOf('day');
    const rangeStart = dayStart.add(START_HOUR, 'hour');
    const rangeEnd = dayStart.add(END_HOUR, 'hour');
    const totalMinutes = (END_HOUR - START_HOUR) * 60;

    const normalizeSegments = ({ baseStart, baseEnd, directResourceId, orderedRaw }) => {
      if (directResourceId) {
        return [{ resourceId: directResourceId, start: baseStart, end: baseEnd }];
      }

      const items = (Array.isArray(orderedRaw) ? orderedRaw : [])
        .map(seg => ({
          resourceId: toIdString(seg?.resourceId),
          startOffsetMinutes: Number.isFinite(Number(seg?.startOffsetMinutes))
            ? Number(seg.startOffsetMinutes)
            : 0,
          durationMinutes: Number.isFinite(Number(seg?.durationMinutes)) ? Number(seg.durationMinutes) : null,
          orderPosition: Number(seg?.orderPosition || 0),
        }))
        .filter(x => x.resourceId)
        .sort((a, b) => a.startOffsetMinutes - b.startOffsetMinutes || a.orderPosition - b.orderPosition);

      if (items.length === 0) return [];

      return items.map((seg, idx) => {
        const s = baseStart.add(seg.startOffsetMinutes, 'minute');
        const next = items[idx + 1];
        const nextStart = next ? baseStart.add(next.startOffsetMinutes, 'minute') : null;
        const e = seg.durationMinutes != null ? s.add(seg.durationMinutes, 'minute') : nextStart || baseEnd;
        return { resourceId: seg.resourceId, start: s, end: e };
      });
    };

    const blocks = [];

    for (const b of directBookings) {
      const baseStart = dayjs(b?.startTime);
      const baseEnd = dayjs(b?.endTime);
      if (!baseStart.isValid() || !baseEnd.isValid() || !baseEnd.isAfter(baseStart)) continue;

      const directResourceId = toIdString(b?.resourceId);
      const segments = normalizeSegments({ baseStart, baseEnd, directResourceId, orderedRaw: [] });
      if (segments.length === 0) continue;

      const clampedStart = maxDayjs(baseStart, rangeStart);
      const clampedEnd = minDayjs(baseEnd, rangeEnd);
      if (!clampedEnd.isAfter(clampedStart)) continue;

      const startMin = clampNumber(clampedStart.diff(rangeStart, 'minute', true), 0, totalMinutes);
      const endMin = clampNumber(clampedEnd.diff(rangeStart, 'minute', true), 0, totalMinutes);

      const segsClamped = segments
        .map(s => ({
          resourceId: s.resourceId,
          startMin: clampNumber(maxDayjs(s.start, rangeStart).diff(rangeStart, 'minute', true), 0, totalMinutes),
          endMin: clampNumber(minDayjs(s.end, rangeEnd).diff(rangeStart, 'minute', true), 0, totalMinutes),
        }))
        .filter(s => s.endMin > s.startMin);

      if (segsClamped.length === 0) continue;

      const label = (() => {
        const first = segsClamped[0];
        return first ? getResourceLabel(first.resourceId) : '—';
      })();

      blocks.push({
        key: toIdString(b?._id) || b?.bookingNumber || `${b?.startTime}-${b?.endTime}`,
        startMin,
        endMin,
        timeLabel: `${baseStart.format('HH:mm')}–${baseEnd.format('HH:mm')}`,
        status: b?.status || '',
        label,
        segments: segsClamped,
      });
    }

    blocks.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

    const rowEnds = [];
    const blocksWithRow = blocks.map(block => {
      let row = 0;
      for (; row < rowEnds.length; row += 1) {
        if (block.startMin >= rowEnds[row]) break;
      }
      if (row === rowEnds.length) rowEnds.push(block.endMin);
      rowEnds[row] = Math.max(rowEnds[row], block.endMin);
      return { ...block, row };
    });

    const times = Array.from({ length: (END_HOUR - START_HOUR) * 2 + 1 }, (_, index) => {
      const hour = START_HOUR + Math.floor(index / 2);
      const minutes = index % 2 === 0 ? '00' : '30';
      return {
        i: index,
        time: `${hour}:${minutes}`,
        label: index % 2 === 0 ? String(hour).padStart(2, '0') : '30',
      };
    });

    return {
      config: { BAR_HEIGHT, BAR_GAP },
      times,
      blocks: blocksWithRow,
      rowCount: rowEnds.length,
      totalMinutes,
    };
  }, [internalBookings, resourceById]);

  const renderTimeline = () => {
    const BAR_HEIGHT = timeline.config.BAR_HEIGHT;
    const BAR_GAP = timeline.config.BAR_GAP;
    const totalMinutes = timeline.totalMinutes || 1;
    const minTimelineWidth = Math.max(850, timeline.times.length * 22);
    const timelineContentWidth = Math.max(timelineViewportWidth || 0, minTimelineWidth);
    const pxPerMinute = timelineContentWidth > 0 ? timelineContentWidth / totalMinutes : 0;
    const barAreaHeight =
      timeline.rowCount > 0 ? timeline.rowCount * (BAR_HEIGHT + BAR_GAP) - BAR_GAP : BAR_HEIGHT;

    const statusOpacity = status => {
      const s = String(status || '').toLowerCase();
      if (s.includes('await')) return 0.75;
      if (s.includes('confirm')) return 0.95;
      if (s.includes('cancel')) return 0.35;
      return 0.9;
    };

    const showHeader = timeline.times.length > 0;

    return (
      <Box
        ref={timelineViewportRef}
        sx={{
          width: '100%',
          pb: 0.5,
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Box sx={{ width: timelineContentWidth || '100%', minWidth: '100%' }}>
          {showHeader ? (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                border: timelineChrome.outerBorder,
              }}
            >
              {timeline.times.map(t => (
                <Box
                  key={t.i}
                  sx={{
                    flex: '1 0 auto',
                    minWidth: `${timelineContentWidth / Math.max(timeline.times.length, 1)}px`,
                    backgroundColor: timelineChrome.headerBg,
                    color: theme.palette.mode === 'light' ? 'text.primary' : 'common.white',
                    fontSize: t.label === '30' ? '0.75rem' : '0.9rem',
                    fontWeight: t.label === '30' ? 400 : 700,
                    textAlign: 'center',
                    py: 0.35,
                    borderRight: timelineChrome.headerCellBorderRight,
                    userSelect: 'none',
                  }}
                >
                  {t.label}
                </Box>
              ))}
            </Box>
          ) : null}

          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: barAreaHeight,
              backgroundColor: timelineChrome.trackBg,
              borderRadius: 1,
              mt: 0.75,
              border: timelineChrome.outerBorder,
            }}
          >
            {showHeader ? (
              <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                {timeline.times.map((t, idx) => {
                  if (idx === 0) return null;
                  const leftPct = `${(idx / timeline.times.length) * 100}%`;
                  const isHour = t.label !== '30';
                  return (
                    <Box
                      key={`grid-${t.i}`}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: leftPct,
                        width: '1px',
                        backgroundColor: isHour ? timelineChrome.gridHour : timelineChrome.gridHalf,
                      }}
                    />
                  );
                })}
              </Box>
            ) : null}

            {timelineContentWidth > 0
              ? timeline.blocks.map(block => {
                  const left = block.startMin * pxPerMinute;
                  const w = Math.max(4, (block.endMin - block.startMin) * pxPerMinute);
                  const top = block.row * (BAR_HEIGHT + BAR_GAP);
                  const opacity = statusOpacity(block.status);
                  const showLabel = w >= 70;
                  const isRequested = String(block.status || '')
                    .toLowerCase()
                    .includes('await');

                  return (
                    <Box
                      key={block.key}
                      title={`${block.timeLabel} • ${block.label}${
                        isRequested ? ' • angefragt (noch nicht bestätigt)' : ''
                      }`}
                      sx={{
                        position: 'absolute',
                        left,
                        top,
                        width: w,
                        height: BAR_HEIGHT,
                        borderRadius: 1.25,
                        overflow: 'hidden',
                        border: isRequested
                          ? `1px dashed ${theme.palette.warning.main}`
                          : timelineChrome.barBorder,
                        backgroundColor: timelineChrome.barBg,
                        opacity,
                        boxShadow: `0 2px 10px ${alpha(theme.palette.common.black, 0.35)}`,
                      }}
                    >
                      <Box sx={{ position: 'absolute', inset: 0 }}>
                        {block.segments.map((seg, idx) => {
                          const segLeft = Math.max(0, (seg.startMin - block.startMin) * pxPerMinute);
                          const segWidth = Math.max(2, (seg.endMin - seg.startMin) * pxPerMinute);
                          const color = resourceColorById.get(seg.resourceId) || theme.palette.grey[300];
                          return (
                            <Box
                              key={`${block.key}-${seg.resourceId}-${idx}`}
                              sx={{
                                position: 'absolute',
                                left: segLeft,
                                top: 0,
                                height: '100%',
                                width: segWidth,
                                backgroundColor: color,
                              }}
                            />
                          );
                        })}
                      </Box>

                      {isRequested ? (
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            backgroundImage: REQUESTED_HATCH_IMAGE,
                          }}
                        />
                      ) : null}

                      {showLabel ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            px: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '100%',
                              px: 0.85,
                              py: 0.2,
                              borderRadius: 1,
                              backgroundColor: timelineChrome.labelBg,
                              color:
                                theme.palette.mode === 'light' ? 'text.primary' : 'common.white',
                              fontSize: '0.8rem',
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              textShadow:
                                theme.palette.mode === 'light'
                                  ? 'none'
                                  : `0 1px 2px ${alpha(theme.palette.common.black, 0.75)}`,
                              border:
                                theme.palette.mode === 'light'
                                  ? `1px solid ${theme.palette.divider}`
                                  : 'none',
                            }}
                          >
                            {block.label}
                          </Box>
                        </Box>
                      ) : null}
                    </Box>
                  );
                })
              : null}

            {timeline.blocks.length === 0 ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant='body2' sx={{ opacity: 0.8 }}>
                  Keine Buchungen mit zugewiesener Ressource gefunden.
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  };

  if (!show) return null;

  return (
    <Accordion sx={{ mt: 1 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 600 }}>Ressourcenbuchungen</Typography>
          <Chip
            size='small'
            label={`${internalBookings.filter(b => b?.resourceId != null).length}`}
            sx={{ ml: 1, backgroundColor: timelineChrome.chipBg }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant='caption' sx={{ opacity: 0.8 }}>
            {resources.length > 0 ? `${resources.length} Ressourcen` : 'keine Ressourcenliste'}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 0.5 }}>
        {resources.length > 0 ? (
          <Box sx={{ mb: 0.5 }}>
            <ResourceLegend
              resources={resources}
              loading={false}
              resourceColorById={resourceColorById}
            />
          </Box>
        ) : null}

        <Divider sx={{ mb: 0.5 }} />

        {renderTimeline()}
      </AccordionDetails>
    </Accordion>
  );
});

const Resources = () => {
  const authCtx = useContext(AuthContext);
  const showWeekdayInDatePicker = authCtx?.preferences?.showWeekdayInResourcesView !== false;

  const { getResourcesByEntertainmentsDay } = useInternalBooking();
  const { getBranches, getAllEntertainmentsOfBranches } = useBranch();
  const { getWorktimeByBranchDay } = useWorktime();

  // Zustandsvariablen
  const [date, setDate] = useState(dayjs());
  const [branch, setBranch] = useState([]);
  const [entertainment, setEntertainment] = useState([]);
  const [data, setData] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [compactView, setCompactView] = useState(false);
  const [worktimeData, setWorktimeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const fetchData = async () => {
    const dateReq = date.format('YYYY-MM-DD');

    if (!branch.length || !entertainment.length) {
      setData([]);
      return;
    }

    const branchIds = branch.map(branch => branch.id);
    const entertainments = entertainment.map(entertainment => entertainment.label);

    setIsLoading(true);
    try {
      const resources = await getResourcesByEntertainmentsDay({
        date: dateReq,
        branches: branchIds,
        entertainments: entertainments,
      });

      setData(resources);
    } catch (error) {
      console.log('Fehler beim Abrufen der Ressourcen:', error);
      toast.error('Fehler beim Abrufen der Ressourcen-Auslastung');
    } finally {
      setIsLoading(false);
    }

    if (branch.length === 1 && authCtx.checkRight('worktime:read')) {
      try {
        const worktimes = await getWorktimeByBranchDay({
          branchId: branchIds[0],
          day: dateReq,
        });
        setWorktimeData(worktimes.worktimes);
      } catch (error) {
        console.log('Fehler beim Abrufen der Arbeitszeiten:', error);
        toast.error('Fehler beim Abrufen der Arbeitszeiten');
      }
    } else {
      setWorktimeData(null);
    }
  };

  useEffect(() => {
    const f = async () => {
      const branches = await getBranches();
      let shownBranches = branches.map(branch => ({
        label: branch.locationName,
        id: branch._id,
      }));
      setBranchOptions([{ label: 'Alles auswählen', id: 'all' }, ...shownBranches]);
      if (shownBranches.length === 1) {
        setBranch(shownBranches);
      }
    };
    f();
  }, []);

  useEffect(() => {
    if (!branch.length) return;
    const f = async () => {
      const branchIds = branch.map(branch => branch.id);
      const allEntertainments = await getAllEntertainmentsOfBranches(branchIds);
      const shownEntertainments = allEntertainments.map(entertainment => ({
        label: entertainment,
      }));
      setEntertainmentOptions([{ label: 'Alles auswählen' }, ...shownEntertainments]);
      if (branch.length === 1) {
        setEntertainment(shownEntertainments);
      } else {
        setEntertainment([]);
        setWorktimeData(null);
      }
    };
    f();
  }, [branch]);

  useEffect(() => {
    fetchData();
  }, [date, branch, entertainment]);

  const handleDayChange = (interval, direction) => {
    if (direction === 'next') {
      setDate(currentDate => currentDate.add(interval, 'day'));
    } else {
      setDate(currentDate => currentDate.subtract(interval, 'day'));
    }
  };

  const handleTodayClick = () => {
    setDate(dayjs());
  };

  const handleDateChange = newValue => {
    if (newValue?.isValid?.()) {
      setDate(newValue);
    }
  };

  const handleBranchChange = (_event, newValue) => {
    if (newValue.some(option => option.id === 'all')) {
      setBranch(branchOptions.slice(1));
    } else {
      setBranch(newValue);
    }
  };

  const handleEntertainmentChange = (_event, newValue) => {
    if (newValue.some(option => option.label === 'Alles auswählen')) {
      setEntertainment(entertainmentOptions.slice(1));
    } else {
      setEntertainment(newValue);
    }
  };

  const handleCompactSwitch = event => {
    setCompactView(event.target.checked);
  };

  return (
    <Box
      sx={{
        ...viewRootTextPrimarySx,
        padding: 2,
        backgroundColor: 'background.default',
        minHeight: '100vh',
      }}
    >
      <Paper sx={{ marginBottom: 2, padding: 2 }} elevation={3}>
        <Grid container rowSpacing={2} columnSpacing={2} alignItems='center'>
          <Grid sm={branchOptions.length === 2 ? 6 : 12} xs={12}>
            <Stack
              direction='row'
              spacing={2}
              useFlexGap
              flexWrap='wrap'
              alignItems='center'
            >
              <DatePicker
                label='Datum'
                value={date}
                format='DD.MM.YYYY'
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: { xs: showWeekdayInDatePicker ? 210 : 150, sm: showWeekdayInDatePicker ? 230 : 150 } },
                    InputProps: showWeekdayInDatePicker
                      ? {
                          startAdornment: (
                            <InputAdornment position='start'>{date.format('ddd')}</InputAdornment>
                          ),
                        }
                      : undefined,
                  },
                }}
                onChange={handleDateChange}
              />
              <Hidden smDown>
                <IconButton size='small' onClick={() => handleDayChange(1, 'previous')}>
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton size='small' onClick={() => handleDayChange(1, 'next')}>
                  <NavigateNextIcon />
                </IconButton>
                <IconButton size='small' onClick={() => handleDayChange(7, 'previous')}>
                  <KeyboardDoubleArrowLeftIcon />
                </IconButton>
                <IconButton size='small' onClick={() => handleDayChange(7, 'next')}>
                  <KeyboardDoubleArrowRightIcon />
                </IconButton>
              </Hidden>
              <Button
                variant='outlined'
                color='primary'
                startIcon={<TodayIcon />}
                onClick={handleTodayClick}
                sx={{ minWidth: 'fit-content' }}
              >
                Heute
              </Button>
              <Stack alignItems={'center'} spacing={0}>
                <Typography variant='body2'>Kompakt</Typography>
                <Switch checked={compactView} onChange={handleCompactSwitch} size='small' />
              </Stack>
            </Stack>
          </Grid>
          {branchOptions.length > 2 && (
            <Grid sm={6} xs={12}>
              <Autocomplete
                multiple
                id='branch'
                options={branchOptions}
                limitTags={isSmallScreen ? 1 : 3}
                getOptionLabel={option => option.label}
                sx={{ width: '100%' }}
                value={branch}
                onChange={handleBranchChange}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={params => (
                  <TextField {...params} label='Branches' variant='outlined' />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
              />
            </Grid>
          )}
          <Grid sm={6} xs={12}>
            <Autocomplete
              multiple
              id='entertainment'
              options={entertainmentOptions}
              limitTags={isSmallScreen ? 1 : 3}
              getOptionLabel={option => option.label}
              sx={{ width: '100%' }}
              value={entertainment}
              onChange={handleEntertainmentChange}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              renderInput={params => (
                <TextField {...params} label='Entertainments' variant='outlined' />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
      <LegendGradient />
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1.5, mt: -0.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 22,
            height: 14,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor:
              theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.grey[700],
            backgroundImage: REQUESTED_HATCH_IMAGE,
            flexShrink: 0,
          }}
        />
        <Typography variant='caption' sx={{ opacity: 0.8 }}>
          Schraffur = angefragte, noch nicht bestätigte Buchung (Slot bleibt blockiert)
        </Typography>
      </Stack>
      {isLoading ? (
        <DotsLoader />
      ) : (
        <>
          {data.map(entertainmentData => (
            <Box key={entertainmentData.entertainmentName} sx={{ pt: '20px' }}>
              <EntertainmentCapacity data={entertainmentData} condensedMode={compactView} />
              <ResourceBookingsAccordion entertainmentData={entertainmentData} />
            </Box>
          ))}
          {worktimeData && (
            <Box sx={{ pt: '20px' }}>
              <WorktimeTimeline worktimes={worktimeData} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Resources;
